/**
 * The "Minimum DPI/PPI" filter's URL segment — `min-dpi-300-70x100cm`.
 *
 * ONE definition because three places have to agree on it: `Chrome.tsx` prefills the four
 * controls from the URL on the server, `navigation.ts` writes the segment into the URL on
 * the client, and `filterBuilder.ts` turns it into the pixel predicate. Two hand-rolled
 * regexes drift, and a filter that reads back differently from how it was written is a
 * link that silently returns different results for the person you sent it to.
 *
 * The same string IS the filter's wire value, so a shared link and a live search go through
 * exactly one parser.
 */
/** Height, width and DPI are all clamped to this range, as the form states. */
export const PRINT_SIZE_MIN = 1;
export const PRINT_SIZE_MAX = 999;
/** cm per inch — the only conversion this filter needs. */
const CM_PER_INCH = 2.54;
/**
 * `min-dpi-<dpi>-<height>x<width><unit>`.
 *
 * Numbers are re-serialised from the parsed values, so `007` normalises to `7` and the
 * segment written for a given selection is always the same string.
 */
export function formatPrintSizeSegment(sel) {
    return `min-dpi-${sel.dpi}-${sel.height}x${sel.width}${sel.unit}`;
}
/**
 * Parse one URL segment, or null if it is not a well-formed print-size segment.
 *
 * Deliberately total: a hand-edited or truncated URL yields null and the search simply runs
 * unfiltered, rather than erroring or matching nothing. The `\d{1,3}` bound plus the range
 * check is the whole input surface — there is nothing here a crafted segment can reach.
 */
export function parsePrintSizeSegment(segment) {
    const m = /^min-dpi-(\d{1,3})-(\d{1,3})x(\d{1,3})(cm|in)$/.exec(segment.trim());
    if (!m)
        return null;
    const dpi = Number(m[1]);
    const height = Number(m[2]);
    const width = Number(m[3]);
    const inRange = (n) => n >= PRINT_SIZE_MIN && n <= PRINT_SIZE_MAX;
    if (!inRange(dpi) || !inRange(height) || !inRange(width))
        return null;
    return { height, width, unit: m[4], dpi };
}
/** The first print-size segment in a URL's path segments, or null. */
export function findPrintSizeSegment(segments) {
    for (const seg of segments) {
        const found = parsePrintSizeSegment(seg);
        if (found)
            return found;
    }
    return null;
}
/**
 * The pixel dimensions a print of this size needs.
 *
 * DPI is dots per INCH, so a centimetre size converts first. `ceil`, not `round`: 299.4
 * pixels does not print at the requested density, and the filter promises a MINIMUM.
 */
export function printSizePixels(sel) {
    const toInches = (n) => (sel.unit === "cm" ? n / CM_PER_INCH : n);
    return {
        wPx: Math.ceil(toInches(sel.width) * sel.dpi),
        hPx: Math.ceil(toInches(sel.height) * sel.dpi),
    };
}
//# sourceMappingURL=printSize.js.map